import React, { useCallback, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Popconfirm,
  Tooltip,
} from '@surecompanies/core_components'
import RHFInput from '../../PlanBenefitOverrides/components/form/RHFInput'
import RHFSelect from '../../PlanBenefitOverrides/components/form/RHFSelect'
import RHFToggle from '../../PlanBenefitOverrides/components/form/RHFToggle'
import { useDebounce } from 'hooks/useDebounce'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import {
  PlanSmokerRateOverrideFormSchema,
  apiPayloadToForm,
  flatToApiPayload,
  type SmokerRateViewMetadata,
  type PlanSmokerRateOverrideFormValues,
} from './planSmokerRateOverrideForm.schema'
import { US_STATE_OPTIONS } from '../../../constants'
import {
  useCheckDuplicateSmokerRateOverrideRule,
  SMOKER_RATE_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY,
} from '../hooks/useCheckDuplicateSmokerRateOverrideRule'
import { useDeleteSmokerRateOverrideRule } from '../hooks/useDeleteSmokerRateOverrideRule'
import { useQueryClient } from '@tanstack/react-query'
import type { CreatePlanSmokerRateOverrideRuleRequestDto } from '../../../api/dto/plan-smoker-rate-override-rules.dto'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import styles from '../../../components/PlanOverridesComponents.module.scss'

const DUPLICATE_CHECK_DEBOUNCE_MS = 400
const CURRENT_YEAR = new Date().getFullYear()

export type SmokerRateFormMode = 'create' | 'edit' | 'view'

export interface SmokerRateServerFieldError {
  field: string
  errors: string[]
}

interface Props {
  mode?: SmokerRateFormMode
  defaultValues?: Partial<PlanSmokerRateOverrideFormValues>
  serverErrors?: SmokerRateServerFieldError[]
  metadata?: SmokerRateViewMetadata
  onSubmit?: (data: CreatePlanSmokerRateOverrideRuleRequestDto) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  onDuplicateFound?: (ruleId: string) => void
  extraContent?: React.ReactNode
  step?: number
}

export default function PlanSmokerRateOverrideForm({
  mode = 'create',
  defaultValues,
  serverErrors,
  metadata,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onDuplicateFound,
  extraContent,
  step,
}: Props) {
  const isEditMode = mode === 'edit'
  const isReadOnly = mode === 'view'
  const isCreateMode = mode === 'create'

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, submitCount },
  } = useForm<PlanSmokerRateOverrideFormValues>({
    resolver: zodResolver(PlanSmokerRateOverrideFormSchema),
    defaultValues: {
      year: CURRENT_YEAR,
      state: '',
      issuerId: '',
      isActive: false,
      sourceUrl: '',
      notes: '',
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return
    serverErrors.forEach(({ field, errors: fieldErrors }) => {
      if (!field || fieldErrors.length === 0) return
      const mappedField = field.startsWith('match.')
        ? (field.slice(
            'match.'.length
          ) as keyof PlanSmokerRateOverrideFormValues)
        : field.startsWith('params.')
          ? (field.slice(
              'params.'.length
            ) as keyof PlanSmokerRateOverrideFormValues)
          : (field as keyof PlanSmokerRateOverrideFormValues)
      setError(mappedField, { type: 'server', message: fieldErrors.join(', ') })
    })
  }, [serverErrors, setError])

  const year = useWatch({ control, name: 'year' })
  const state = useWatch({ control, name: 'state' })
  const age = useWatch({ control, name: 'age' })
  const issuerId = useWatch({ control, name: 'issuerId' })
  const increaseRate = useWatch({ control, name: 'increaseRate' })

  const duplicateKey =
    isCreateMode &&
    year &&
    state &&
    age !== undefined &&
    String(age) !== '' &&
    age >= 0 &&
    issuerId
      ? `${year}|${state}|${age}|${issuerId}`
      : null
  const debouncedDuplicateKey = useDebounce(
    duplicateKey,
    DUPLICATE_CHECK_DEBOUNCE_MS
  )
  const duplicateQuery = debouncedDuplicateKey
    ? { year: Number(year), state, age: Number(age), issuerId }
    : {}
  const { data: duplicateData } = useCheckDuplicateSmokerRateOverrideRule(
    duplicateQuery,
    Boolean(debouncedDuplicateKey)
  )
  const showDuplicateAlert = duplicateData?.exists === true
  const duplicateRule = duplicateData?.exists ? duplicateData.rule : null

  const queryClient = useQueryClient()
  const { mutate: deleteDuplicate, isPending: isDeletingDuplicate } =
    useDeleteSmokerRateOverrideRule()

  const handleDeleteDuplicate = useCallback(() => {
    if (!duplicateRule) return
    deleteDuplicate(duplicateRule.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [SMOKER_RATE_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY],
        })
      },
    })
  }, [duplicateRule, deleteDuplicate, queryClient])

  const handleFormSubmit = async (values: PlanSmokerRateOverrideFormValues) => {
    if (!onSubmit) return
    const payload = flatToApiPayload(values)
    await onSubmit(payload)
  }

  const formHasErrors = submitCount > 0 && Object.keys(errors).length > 0

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {isCreateMode && showDuplicateAlert && duplicateRule && (
        <div className={styles.duplicateBanner}>
          <div className={styles.duplicateBannerIcon}>⚠</div>
          <div className={styles.duplicateBannerBody}>
            <span className={styles.duplicateBannerTitle}>
              Rule already exists for this combination
            </span>
            <span className={styles.duplicateBannerValue}>
              {duplicateRule.match.state}
              <span className={styles.duplicateBannerDot}> · </span>
              Age {duplicateRule.match.age}
              <span className={styles.duplicateBannerDot}> · </span>
              Issuer {duplicateRule.match.issuerId}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.year}
            </span>
            <span className={styles.duplicateBannerMeta}>
              <span
                className={`${styles.statusTag} ${duplicateRule.status === 'APPROVED' ? styles.statusActive : duplicateRule.status === 'REJECTED' ? styles.statusInactive : styles.statusDraft}`}
              >
                {duplicateRule.status}
              </span>
              <span className={styles.duplicateBannerDot}>·</span>
              {relativeTimeString(duplicateRule.updatedAt)}
            </span>
          </div>
          <div className={styles.duplicateBannerActions}>
            <Button
              type="primary"
              size="small"
              onClick={() => onDuplicateFound?.(duplicateRule.id)}
              disabled={!onDuplicateFound}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              description="This will permanently delete the existing rule."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={handleDeleteDuplicate}
            >
              <Button
                type="default"
                size="small"
                danger
                loading={isDeletingDuplicate}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {formHasErrors && (
        <Alert
          type="error"
          showIcon
          message="Please fix the errors below before saving."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={styles.stepList}>
        {/* Step 1 — Identity (immutable after create) */}
        {(step === undefined || step === 1) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepRail} />
            </div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>Target</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="year"
                  control={control}
                  label="Year"
                  required
                  type="number"
                  placeholder="e.g. 2025"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFSelect
                  name="state"
                  control={control}
                  label="State"
                  required
                  options={US_STATE_OPTIONS}
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="label"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="age"
                  control={control}
                  label="Age"
                  required
                  type="number"
                  min={0}
                  max={99}
                  placeholder="0–99"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="issuerId"
                  control={control}
                  label="Issuer ID"
                  required
                  maxLength={5}
                  placeholder="e.g. 94506"
                  disabled={isReadOnly || isEditMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Override data (editable) */}
        {(step === undefined || step === 2) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>2</div>
            </div>
            <div className={`${styles.stepContent} ${styles.stepContentLast}`}>
              <p className={styles.stepTitle}>Override data</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="increaseRate"
                  control={control}
                  label="Increase Rate"
                  required
                  inputMode="decimal"
                  placeholder="e.g. 0.250"
                  decimalPlaces={3}
                  disabled={isReadOnly}
                />
                <RHFToggle
                  name="isActive"
                  control={control}
                  label="Is Active"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Evidence documents (rendered via extraContent) */}
        {(step === undefined || step === 3) && extraContent}
      </div>

      {!isReadOnly && (
        <div className={styles.formFooter}>
          {isEditMode && metadata ? (
            <OverrideAuditStrip metadata={metadata} />
          ) : (
            <div />
          )}
          <div className={styles.formFooterActions}>
            <Button type="default" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Tooltip
              title={
                showDuplicateAlert
                  ? 'Resolve the duplicate rule above before saving.'
                  : String(increaseRate ?? '').trim() === ''
                    ? 'Enter an increase rate before saving.'
                    : !isEditMode && !year
                      ? 'Enter a year before saving.'
                      : !isEditMode && !state
                        ? 'Select a state before saving.'
                        : !isEditMode &&
                            (age === undefined || String(age) === '' || age < 0)
                          ? 'Enter a valid age before saving.'
                          : !isEditMode && !issuerId
                            ? 'Enter an issuer ID before saving.'
                            : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  showDuplicateAlert ||
                  String(increaseRate ?? '').trim() === '' ||
                  (!isEditMode &&
                    (!year ||
                      !state ||
                      age === undefined ||
                      String(age) === '' ||
                      age < 0 ||
                      !issuerId))
                }
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </form>
  )
}

export { apiPayloadToForm }
